import React from "react"
import { IconButton, FontIcon, RaisedButton, CircularProgress } from "material-ui"
import autobind from "class-autobind"
import AccountStore from "../../../stores/AccountStore"
import strings from "../../../localization/strings"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import ConfirmDialogForAllApproveStatus from "./ConfirmDialogForAllApproveStatus"
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore"
import moment from "moment"
import * as permissionHelper from "../../../helpers/permissionHelper"
import { ETimecardPageApproveOptions, ETimecardApproveStatus, ITimecardDates, ITimecardFullData, EPunchSetApprovalType, IDate, IPayrollPayCalendar, IJobDocument, EExceptionType, IUserFilter } from "../../../models"
import AppStateStore from "../../../stores/AppStateStore"
import TimecardsTable from "./TimecardsTable"
import GlobalPunchAdd from "../../punch/global_punch/GlobalPunchAdd"
import GlobalPunchEdit from "../../punch/global_punch/GlobalPunchEdit"
import * as approveActions from "../../../actions/ApproveActions"
import TimecardReport from "../timecard_report/TimecardReport"
import * as timecardHelper from "../../../helpers/timecardHelper"
import * as payrollHelper from "../../../helpers/payrollHelper"

interface IProps {
	referenceDate: IDate
	userFilter: IUserFilter
	timecards: ITimecardFullData[]
	totalTimecards: number
	total: number
	offset: number
	limit: number
	limitOptions: number[]
	selectedPayrollPayCalendar: IPayrollPayCalendar
	showTimecardDetails: boolean
	onPageClick(offset: number): void
	onLimitChange(limit: number): void
	reLoadTimecards(cb?: () => void): void

	// timecards page
	timecardDates?: ITimecardDates
	filterByDefaultHierarchy?: boolean
	filterDirectReportsOnly?: boolean
	filterByPunchHierarchy?: boolean
	filterByException?: boolean
	filterExceptions?: EExceptionType[]
	reloadTable?(): void

	// job -> timecard list
	job?: IJobDocument
}
interface IState {
	selectedTimecards: ITimecardFullData[]
	confirmApproveStatusForAll: ETimecardApproveStatus
	loading: boolean
	expandAllTimecards: boolean
}

export default class TimecardsBody extends React.Component<IProps, IState> {
	private TimecardsTable
	constructor(props: IProps) {
		super(props)
		this.state = {
			selectedTimecards: [],
			confirmApproveStatusForAll: null,
			loading: false,
			expandAllTimecards: false,
		}
		autobind(this)
	}

	handleApproveStatusForOneTimecard(timecard: ITimecardFullData, status, dayOfWeekIndex = -1) {
		// supervisor cannot approve/unapprove his own timecard
		if (timecard.Timecard.Employee.Id === AuthenticationStore.getLoggedInUser().Id) {
			AppStateStore.setAlertDialogContent({
				title: strings.CannotApproveTimecardsTitle,
				content: status === "approve" ? strings.Supervisor_cannot_approve_his_own_timecard : strings.Supervisor_cannot_unapprove_his_own_timecard
			})
			return
		}

		const allowApprovePunchSet = permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ApproveTimecards)
		const allowApproveExpenses = permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.ApproveEmployeeExpense);

		if (this.props.job) {
			!allowApprovePunchSet ?
				AppStateStore.setProgressSnackbarContent(strings.You_have_not_a_permission_to_approve_punchSet) :
				status === "approve" ?
					AppStateStore.setConfirmDialogContent({
						title: strings.Approve_Timecard,
						content: strings.Are_you_sure_you_want_to_approve_the_timecard,
						confirm: () => this.onApproveStatusTimecards([timecard.Timecard.Id], "approve", dayOfWeekIndex)
					}) :
					status === "unapprove" &&
					AppStateStore.setConfirmDialogContent({
						title: strings.Unapprove_Timecard,
						content: strings.Are_you_sure_you_want_to_unapprove_timecard,
						confirm: () => this.onApproveStatusTimecards([timecard.Timecard.Id], "unapprove", dayOfWeekIndex)
					})
		}
		else {
			if (!allowApprovePunchSet && !(AccountStore.getTimecardTimeExpensesEnabled() && allowApproveExpenses))
				return

			const hasPunchSets = timecard.Timecard.MasterTimecard.FullPunchSets.length > 0
			const hasExpenses = timecardHelper.getTimecardAllExpenses(timecard.Timecard.MasterTimecard).length > 0

			!hasExpenses && !allowApprovePunchSet ?
				AppStateStore.setProgressSnackbarContent(strings.You_have_not_a_permission_to_approve_punchSet) :
				!hasPunchSets && !allowApproveExpenses ?
					AppStateStore.setProgressSnackbarContent(strings.You_have_not_a_permission_to_approve_expenses) :
					status === "approve" ?
						AppStateStore.setConfirmDialogContent({
							title: strings.Approve_Timecard,
							content: strings.Are_you_sure_you_want_to_approve_the_timecard,
							confirm: () => this.onApproveStatusTimecards([timecard.Timecard.Id], "approve", dayOfWeekIndex)
						}) :
						status === "unapprove" &&
						AppStateStore.setConfirmDialogContent({
							title: strings.Unapprove_Timecard,
							content: strings.Are_you_sure_you_want_to_unapprove_timecard,
							confirm: () => this.onApproveStatusTimecards([timecard.Timecard.Id], "unapprove", dayOfWeekIndex)
						})
		}
	}

	onApproveStatusTimecards(objectTimecardIds: string[], status, dayOfWeekIndex = -1) {
		status === "approve" ?
			approveActions.handleApproveTimecard(
				objectTimecardIds,
				this.props.job ? this.props.job.Id : "",
				dayOfWeekIndex,
				EPunchSetApprovalType.Supervisor,
				(msg) => this.props.reLoadTimecards(() => AppStateStore.setProgressSnackbarContent(msg))
			) :
			approveActions.handleUnapproveTimecard(
				objectTimecardIds,
				this.props.job ? this.props.job.Id : "",
				dayOfWeekIndex,
				EPunchSetApprovalType.Supervisor,
				(msg) => this.props.reLoadTimecards(() => AppStateStore.setProgressSnackbarContent(msg))
			)
	}

	onApproveStatusForSelectedTimecards(status) {
		const objectTimecardIds = this.state.selectedTimecards.map(item => item.Timecard.Id)
		const checkedAll = this.props.job ? false : this.state.selectedTimecards.length === this.props.total
		status === "approve" && (
			(checkedAll && this.props.totalTimecards > this.props.limit) ?
				this.onApproveStatusTimecards(objectTimecardIds, "approve") :
				AppStateStore.setConfirmDialogContent({
					title: strings.Approve_Timecard,
					content: strings.Are_you_sure_you_want_to_approve_the_timecard,
					confirm: () => this.onApproveStatusTimecards(objectTimecardIds, "approve")
				})
		)
		status === "unapprove" && (
			(checkedAll && this.props.totalTimecards > this.props.limit) ?
				this.onApproveStatusTimecards(objectTimecardIds, "unapprove") :
				AppStateStore.setConfirmDialogContent({
					title: strings.Unapprove_Timecard,
					content: strings.Are_you_sure_you_want_to_unapprove_timecard,
					confirm: () => this.onApproveStatusTimecards(objectTimecardIds, "unapprove")
				})
		)
	}

	onApproveStatusForAllTimecards(status) {
		const payGroupIds = payrollHelper.getPayGroupIdsByPayrollPayCalendar(this.props.selectedPayrollPayCalendar)
		status === "approve" ?
			approveActions.handleApproveAllTimecards(
				AccountStore.getCurrentAccount().Id,
				moment(this.props.timecardDates.StartDate).add(1, "day").format(),
				HierarchySettingsStore.getGlobalLevelItemFilterId(),
				this.props.filterDirectReportsOnly,
				this.props.filterByDefaultHierarchy,
				this.props.filterByException,
				payGroupIds,
				this.props.filterExceptions,
				(msg) => this.props.reLoadTimecards(() => AppStateStore.setProgressSnackbarContent(msg))
			) :
			approveActions.handleUnapproveAllTimecards(
				AccountStore.getCurrentAccount().Id,
				moment(this.props.timecardDates.StartDate).add(1, "day").format(),
				HierarchySettingsStore.getGlobalLevelItemFilterId(),
				this.props.filterDirectReportsOnly,
				this.props.filterByDefaultHierarchy,
				this.props.filterByException,
				payGroupIds,
				this.props.filterExceptions,
				(msg) => this.props.reLoadTimecards(() => AppStateStore.setProgressSnackbarContent(msg))
			)
	}

	handleApproveStatusForSelectedTimecards(status: ETimecardApproveStatus) {
		this.state.selectedTimecards.find(item => item.Timecard.Employee.Id === AuthenticationStore.getLoggedInUser().Id) ?
			AppStateStore.setAlertDialogContent({
				title: strings.CannotApproveTimecardsTitle,
				content: status === ETimecardApproveStatus.Approve ? strings.Supervisor_cannot_approve_his_own_timecard : strings.Supervisor_cannot_unapprove_his_own_timecard
			}) :
			!this.props.job && this.state.selectedTimecards.length === this.props.total && this.props.totalTimecards > this.props.limit ?
				this.setState({ confirmApproveStatusForAll: status }) :
				this.onApproveStatusForSelectedTimecards(status)
	}

	handleConfirmForAllApproveStatus(selectedOption: ETimecardPageApproveOptions, status: ETimecardApproveStatus) {
		this.setState({ confirmApproveStatusForAll: null })
		switch (selectedOption) {
			case ETimecardPageApproveOptions.ApproveStatusForAll:
				this.onApproveStatusForAllTimecards(status)
				break
			case ETimecardPageApproveOptions.ApproveStatusForPage:
				this.onApproveStatusForSelectedTimecards(status)
				break
		}
	}

	reloadPage() {
		this.TimecardsTable.setState({ selectedTimecards: [] }, () => {
			this.props.reLoadTimecards()
			this.setState({ selectedTimecards: [] })
		})
	}

	render() {
		return (
			<div className="TimecardsTable">
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<RaisedButton
						secondary
						style={{ margin: "10px 10px 10px 30px" }}
						disabled={this.props.totalTimecards === 0}
						label={!this.state.expandAllTimecards ? strings.Expand_All_Timecards : strings.Collpase_All_Timecards}
						onClick={() => {
							this.setState({ loading: true }, () => {
								const currTop = window.scrollY
								const currLeft = window.scrollX
								this.setState({ expandAllTimecards: !this.state.expandAllTimecards }, () => {
									setTimeout(() => {
										this.setState({ loading: false }, () => window.scrollTo(currLeft, currTop))
									}, 10)

								})
							})
						}}
					/>
					<div style={{ display: "flex" }}>
						{this.state.selectedTimecards.length > 0 && [
							(
								permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.ApproveTimecards) ||
								(!this.props.job && AccountStore.getTimecardTimeExpensesEnabled() && permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.ApproveEmployeeExpense))
							) && [
								<IconButton data-intercom-target={"menu-Timecards-Btn-ApproveTimecard"} key="approve" tooltip={strings.Approve} onTouchTap={() => this.handleApproveStatusForSelectedTimecards(ETimecardApproveStatus.Approve)}>
									<FontIcon className="fa fa-check" style={{ fontSize: 20 }} />
								</IconButton>,
								<IconButton data-intercom-target={"menu-Timecards-Btn-UnApproveTimecard"} key="unapprove" tooltip={strings.Unapprove} onTouchTap={() => this.handleApproveStatusForSelectedTimecards(ETimecardApproveStatus.UnApprove)}>
									<FontIcon className="fa fa-times" style={{ fontSize: 20 }} />
								</IconButton>
							],
							!this.props.job &&
							permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.GlobalPunchAdd) &&
							<div style={{ paddingTop: 6 }} key="global_punch_add">
								<GlobalPunchAdd
									style={{ display: "inline-block" }}
									employees={this.state.selectedTimecards.map(i => i.Timecard.Employee)}
									reloadPage={this.reloadPage}
								/>
							</div>,
							!this.props.job &&
							permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.GlobalPunchEdit) &&
							<div style={{ paddingTop: 6 }} key="global_punch_edit">
								<GlobalPunchEdit
									style={{ display: "inline-block" }}
									globalPunchEmployees={this.state.selectedTimecards.map(i => i.Timecard.Employee)}
									dayStartTime24Hour={this.props.selectedPayrollPayCalendar.PayPeriodDayStartTime24Hour}
									startDate={this.props.timecardDates.StartDate}
									endDate={this.props.timecardDates.EndDate}
									reloadPage={this.reloadPage}
								/>
							</div>
						]}
						<div style={{ marginRight: 10, paddingTop: 4 }}>
							<TimecardReport
								referenceDate={this.props.referenceDate}
								selectedPayrollPayCalendar={this.props.selectedPayrollPayCalendar}
								userFilter={this.props.userFilter}

								filterByDefaultHierarchy={this.props.filterByDefaultHierarchy}
								filterDirectReportsOnly={this.props.filterDirectReportsOnly}
								filterByPunchHierarchy={this.props.filterByPunchHierarchy}
								filterByException={this.props.filterByException}
								filterExceptions={this.props.filterExceptions}

								jobId={this.props.job ? this.props.job.Id : null}
							/>
						</div>
						{this.props.reloadTable &&
							<div data-intercom-target={"menu-Timecards-Btn-ReloadTimeCards"} title={strings.Refresh} style={{ margin: "15px 40px 0px 0px" }} onClick={this.props.reloadTable}>
								<i style={{ fontSize: 25 }} className="fa fa-refresh itemActionButton" />
							</div>
						}
					</div>
				</div>
				<div style={{ clear: "both" }} />
				<div style={{ borderBottom: "solid 1px rgb(224, 224, 224)", height: 1 }} />
				{this.state.loading ?
					<div className="spinner"><CircularProgress /></div> :
					!this.props.timecards.length ?
						<h2 style={{ textAlign: "center", paddingTop: 25, paddingBottom: 25 }}>
							{strings.No_timecard_found}
						</h2> :
						<TimecardsTable
							ref={item => this.TimecardsTable = item}
							job={this.props.job}
							showCheckBox
							readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Timecards.EditTimecards)}
							timecards={this.props.timecards}
							payrollPayCalendar={this.props.selectedPayrollPayCalendar}
							total={this.props.totalTimecards}
							offset={this.props.offset}
							limit={this.props.limit}
							limitOptions={this.props.limitOptions}
							expandAllTimecards={this.state.expandAllTimecards}
							filterByPunchHierarchy={this.props.filterByPunchHierarchy}
							onPageClick={this.props.onPageClick}
							onLimitChange={this.props.onLimitChange}
							reLoadTimecards={this.props.reLoadTimecards}
							handleSelect={selectedTimecards => this.setState({ selectedTimecards })}
							handleApproveStatusForOneTimecard={this.handleApproveStatusForOneTimecard}
							hideInfoIcon={this.props.job ? true : false}
							showTimecardDetails={this.props.showTimecardDetails}
						/>
				}
				{this.state.confirmApproveStatusForAll &&
					<ConfirmDialogForAllApproveStatus
						close={() => this.setState({ confirmApproveStatusForAll: null })}
						status={this.state.confirmApproveStatusForAll}
						allCount={this.props.totalTimecards}
						countOnPage={this.props.total}
						confirm={this.handleConfirmForAllApproveStatus}
					/>
				}
			</div >
		)
	}
}